import { FC, ReactNode, useState } from 'react';

import { Panel } from 'src/components/Panel/Panel';
import { Parking } from 'src/types';

import { AdminBooking, AdminBookingFields } from '../AdminBooking';
import { BookActions } from '../BookActions';
import { BusyBooking, BusyBookingFields, UnbookActions } from '../Busy';
import { Share, ShareActions, ShareFields } from '../Share';

import classes from './ReservedByEmployee.module.scss';
import {
  bookingDisabledReason,
  Mode,
  RoomBookingPageModeSwitcher,
} from './RoomBookingPageModeSwitcher';

export interface ReservedByEmployeeWrapperProps {
  parking: Parking;
  children?: (mode: Mode) => ReactNode;
}

export const ReservedByEmployeeWrapper: FC<ReservedByEmployeeWrapperProps> = ({
  parking,
  children,
}) => {
  const [mode, setMode] = useState<Mode>(Mode.Sharing);
  const bookingIsEnabled = !!parking.status || parking.shared;

  return (
    <div className={classes.container}>
      <RoomBookingPageModeSwitcher
        mode={mode}
        setMode={setMode}
        bookingIsEnabled={bookingIsEnabled}
      />
      {children?.(mode)}
    </div>
  );
};

export interface ReservedByEmployeeProps {
  parking: Parking;
  employeeNumber: string;
  isAdminOfSpace: boolean;
}

export const ReservedByEmployee: FC<ReservedByEmployeeProps> = ({
  parking,
  employeeNumber,
  isAdminOfSpace,
}) => {
  const bookingIsEnabled = !!parking.status || parking.shared;
  const hasPermissionToUnbook =
    parking.status &&
    (parking.status.authorEmployeeNumber === employeeNumber ||
      parking.status.responsibleEmployeeNumber === employeeNumber ||
      isAdminOfSpace);

  return (
    <ReservedByEmployeeWrapper parking={parking}>
      {mode => {
        switch (mode) {
          case Mode.Sharing:
            return (
              <Share placeId={parking.id!}>
                <Panel actions={<ShareActions />}>
                  <ShareFields
                    responsible={parking.responsibleEmployeeNumber!}
                  />
                </Panel>
              </Share>
            );
          case Mode.Booking:
            return (
              <>
                {parking.status ? (
                  <BusyBooking>
                    <Panel
                      actions={hasPermissionToUnbook ? <UnbookActions /> : null}
                    >
                      <BusyBookingFields status={parking.status} />
                    </Panel>
                  </BusyBooking>
                ) : (
                  <AdminBooking>
                    <Panel
                      actions={
                        <BookActions
                          disabledReason={
                            bookingIsEnabled ? undefined : bookingDisabledReason
                          }
                        />
                      }
                    >
                      <AdminBookingFields parking={parking} />
                    </Panel>
                  </AdminBooking>
                )}
              </>
            );
        }
      }}
    </ReservedByEmployeeWrapper>
  );
};
